<template>
  <div class="carousel-container" id="events">
    <div class="events__content container">
    <p class="events__description">
      <b>Explore our lineup of upcoming events!<br></b>
      From networking opportunities to inspiring talks, stay informed and make the most of what's ahead.
    </p>
      <div class="tab-buttons">
        <button
          :class="{'active-tab': activeTab === 'upcoming'}"
          @click="setActiveTab('upcoming')"
        >
          Upcoming
        </button>
        <button
          :class="{'active-tab': activeTab === 'past'}"
          @click="setActiveTab('past')"
        >
          Past
        </button>
      </div>

      <div v-if="activeTab === 'upcoming'">
        <div v-if="upcomingEvents.length === 0" class="no-events-message">
          Stay tuned for upcoming events!
        </div>
        <Carousel
          v-else
          :breakpoints="breakpoints"
          :mouse-drag="true"
          :loop="shouldEnableLoop(upcomingEvents.length)"
          :wrap-around="shouldEnableLoop(upcomingEvents.length)"
          snap-align="center"
          class="carousel-wrapper upcoming"
        >
          <Slide v-for="(event, index) in upcomingEvents" :key="index" class="slider-item">
            <div :class="['carousel-item', event.specialClass]">
              <a :href="event.url" target="_blank">
                <img v-if="event.image" :src="getImageUrl(event.image)" class="item-image" :alt="event.title">
              </a>
              <div class="item-content">
                <h3 class="item-title">{{ event.title }}</h3>
                <p class="item-date">{{ event.dateText }}</p>
                <a class="item-button" :href="event.url" target="_blank">{{ event.button }}</a>
                <a v-if="event.agenda" class="item-button btn-alt" :href="event.agenda">Event Agenda</a>
              </div>
            </div>
          </Slide>
          <template #addons>
            <div class="carousel-navigation" v-if="shouldShowNavigation(upcomingEvents.length)">
              <Navigation class="carousel-nav"/>
            </div>
          </template>
        </Carousel>
      </div>

      <div v-if="activeTab === 'past'">
        <div v-if="pastEvents.length === 0" class="no-events-message">
          Stay tuned for our past events recaps!
        </div>
        <Carousel
          v-else
          :breakpoints="breakpoints"
          :mouse-drag="true"
          :loop="shouldEnableLoop(pastEvents.length)"
          :wrap-around="shouldEnableLoop(pastEvents.length)"
          snap-align="center"
          class="carousel-wrapper past"
        >
          <Slide v-for="(event, index) in pastEvents" :key="index" class="slider-item">
            <div class="carousel-item">
              <a :href="event.url" target="_blank">
                <img v-if="event.image" :src="getImageUrl(event.image)" class="item-image" :alt="event.title">
              </a>
              <div class="item-content">
                <h3 class="item-title">{{ event.title }}</h3>
                <p class="item-date">{{ event.dateText }}</p>
                <a class="item-button" :href="event.url" target="_blank">{{ event.button }}</a>
                <a v-if="event.agenda" class="item-button btn-alt" :href="event.agenda">Event Agenda</a>
              </div>
            </div>
          </Slide>
          <template #addons>
            <div class="carousel-navigation" v-if="shouldShowNavigation(pastEvents.length)">
              <Navigation class="carousel-nav"/>
            </div>
          </template>
        </Carousel>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import eventData from './json/events.json';

const activeTab = ref('upcoming');
const setActiveTab = (tab) => (activeTab.value = tab);
const windowWidth = ref(window.innerWidth);

const allEvents = eventData.map((item) => ({
  title: item.title,
  image: item.image,
  dateText: item.dateText,
  date: new Date(item.date),
  url: item.url,
  button: item.button,
  agenda: item.agenda,
  external: item.external,
  specialClass: item.specialClass
}));

const upcomingEvents = computed(() =>
  allEvents
    .filter((event) => event.date >= new Date())
    .sort((a, b) => a.date - b.date)
);

const pastEvents = computed(() =>
  allEvents
    .filter((event) => event.date < new Date())
    .sort((a, b) => b.date - a.date)
);

// Get the number of items shown based on current window width
const getItemsToShow = () => {
  if (windowWidth.value >= 1400) return 4;
  if (windowWidth.value >= 1279) return 3;
  if (windowWidth.value >= 767) return 2;
  return 1;
};

// Check if navigation should show based on event count and viewport
const shouldShowNavigation = (eventCount) => {
  return eventCount > getItemsToShow();
};

// Check if loop should be enabled
const shouldEnableLoop = (eventCount) => {
  return eventCount > getItemsToShow();
};

// Handle window resize
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const getImageUrl = (name) => {
  return require(`@/assets/events/carousel/${name}`);
};

const breakpoints = {
  500: { itemsToShow: 1, snapAlign: 'center' },
  767: { itemsToShow: 2, snapAlign: 'start' },
  1279: { itemsToShow: 3, snapAlign: 'start' },
  1400: { itemsToShow: 4, snapAlign: 'start' },
};

</script>

<style lang="scss" scoped>
@import "../../styles/import/variables.scss";
@import "../../styles/import/mixins.scss";


#events {
  padding-top: 60px;
}
.events {
  &__content {
    max-width: 1600px;
  }
    &__title {
    color: $white;
    font-size: 40px;
    font-weight: 600;
    margin: 0 0 20px 0;
    line-height: 1.2;
    text-align: center;

    @include upto($breakpoint_lg) {
      font-size: 36px;
    }
    @include upto($breakpoint_sm) {
      font-size: 24px;
    }
  }

  &__description {
    color: $white;
    font-size: 22px;
    font-weight: 300;
    line-height: 33px;
    margin: 0 auto 30px;
    width: 50%;
    b {
      font-weight: 500;
    }

    @include upto($breakpoint_sm) {
      font-size: 16px;
      line-height: 20px;
      width: 100%;
    }
  }
}
.section-title {
  color: $white;
  font-size: 40px;
  font-weight: 600;
  margin: 0 0 20px 0;
  line-height: 1.2;
  @include upto($breakpoint_lg) {
    font-size: 36px;
  }
  @include upto($breakpoint_sm) {
    font-size: 24px;
  }
}

.carousel-container {
  width: 100%;
  background-color: $alt-dark;
  padding: 0 2rem 60px;
  text-align: center;
  @include upto ($breakpoint_sm) {
    padding: 0 0 60px;
  }
}

.title {
  font-size: 24px;
  color: $white;
  margin-bottom: 1rem;
}

.tab-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;

  button {
    padding: 0.5rem 1rem;
    border: none;
    background-color: transparent;
    color: $yellow-hover;
    font-size: 18px;
    font-weight: 400;
    cursor: pointer;
    transition: color 0.3s ease;

    &:not(:last-of-type) {
      margin-right: 1rem;
    }

    &.active-tab {
      color: $white;
      border-bottom: 2px solid $white;
      font-weight: 600;
    }
  }
}

.carousel-wrapper {
  padding: 1rem 0;
}

.slider-item {
  display: flex;
  justify-content: center;
  padding-right: 15px;
  @include upto ($breakpoint_sm) {
    padding-right:5px;
    padding-left:5px;
  }
}

.carousel-wrapper:has(.slider-item:nth-child(-n+4):last-child) .slider-item:last-of-type {
  padding-right: 15px;
}

.carousel-item {
  background-color: $dark-gray;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  justify-content: space-between;
  &.highlighted {
    border: 2px solid $yellow;
  }
}

/*.upcoming {
  .carousel-item {
    @include between(1400px, 1458px) {
      min-height: 410px;
    }
    @include between(768px, 780px) {
      min-height: 395px;
    }
  }
}*/

.item-image {
  width: 100%;
  margin-bottom: 15px;
  border-radius: 10px;
}

.item-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 10px;
}

.item-title {
  color: $white;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  text-align: left;
  @include upto($breakpoint_md) {
    font-size: 16px;
  }
}

.item-date {
  color: $gray;
  font-size: 16px;
  margin: 0 0 10px;
  font-weight: 300;
  line-height: 1.4;
  text-align: left;
  @include upto($breakpoint_md) {
    font-size: 14px;
  }
}

.item-button {
  padding: 10px;
  text-decoration: none;
  background-color: $white;
  color: $alt-dark-gray;
  border: 1px solid $white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  @include transition(0.3s, ease, all);
  &:hover {
    background-color: $yellow-hover;
    color: $white;
    border: 1px solid $alt-dark-gray
  }
  &.btn-alt {
    background-color: transparent;
    border: 1px solid $white;
    color: $white;
    &:hover {
      background-color: $yellow-hover;
      color: $white;
      border: 1px solid $alt-dark-gray
    }
  }
}

.no-events-message {
  font-size: 26px;
  color: $white;
  margin-top: 2rem;
}

.carousel-navigation {
  margin-top: 40px;
  display: flex;
  justify-content: center;
}

:deep(.carousel-nav) {
  display: flex;
  gap: 10px;
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  position: relative;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 20px;
  font-size: 30px !important;
  background-color: $alt-dark;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>